"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PackageCheck, Wrench, FileCheck2, ShieldAlert, ArrowRight, CircleCheck as CheckCircle2, Clock, MapPin, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface ServiceItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  deliverables: string;
  link: string;
  linkText: string;
}
interface SupportBenefit {
  id: string;
  title: string;
  detail: string;
}
const servicesData: ServiceItem[] = [{
  id: "consumables",
  title: "Consumables & Reagent Replenishment",
  badge: "Supply Continuity",
  description: "Scheduled inventory delivery and batch-certified reagents for laboratory spectrometers, chromatography systems, and benchtop testing equipment.",
  deliverables: "Lot-traceable standards, OEM consumable kits, scheduled shipment intervals.",
  link: "/SupportRequestPage",
  linkText: "Request supply schedule"
}, {
  id: "custom-adapters",
  title: "Custom Sampling Adapter Engineering",
  badge: "Process Integration",
  description: "Precision-machined optical interfaces, probe collars, and sanitary flow-cell adapters designed for specialized reactor ports and blend vessels.",
  deliverables: "316L stainless steel fabrication, hygienic sanitary clamp fittings, pressure testing certificates.",
  link: "/ApplicationConsultationPage",
  linkText: "Discuss adapter specs"
}, {
  id: "calibration-validation",
  title: "Instrument Calibration & Validation",
  badge: "GMP Compliance",
  description: "On-site protocol execution including installation qualification (IQ), operational qualification (OQ), and performance verification (PV) against NIST standards.",
  deliverables: "Traceable calibration certificates, audit-ready qualification binders, wavelength accuracy tests.",
  link: "/QualificationLifecycleServicesPage",
  linkText: "View qualification protocols"
}, {
  id: "field-maintenance",
  title: "Field Readiness & Maintenance Support",
  badge: "Lifecycle Operations",
  description: "Scheduled preventive maintenance inspections, optical bench realignment, detector calibration, and rapid on-site troubleshooting.",
  deliverables: "Pre-audit verification reports, genuine component replacements, Structured response process.",
  link: "/PreventiveMaintenanceAndRepairsPage",
  linkText: "Schedule field service"
}];
const supportBenefits: SupportBenefit[] = [{
  id: "coverage",
  title: "Regional Field Deployment",
  detail: "Dedicated technical service engineers stationed across Ireland and the UK."
}, {
  id: "response",
  title: "Documented Service Records",
  detail: "Audit-ready service reports and component certificates delivered within 24 hours of visit."
}, {
  id: "standards",
  title: "Regulated Standards Compliance",
  detail: "Procedures mapped strictly to cGMP, ISO 17025, and relevant pharmacopeia guidelines."
}, {
  id: "uptime",
  title: "Preventive Inspection Plans",
  detail: "Standardized service agreements with pre-scheduled annual or semi-annual maintenance."
}];
export default function ServicesSupport() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (id: string) => {
    switch (id) {
      case "consumables":
        return <PackageCheck className="size-5 text-primary" />;
      case "custom-adapters":
        return <Wrench className="size-5 text-primary" />;
      case "calibration-validation":
        return <FileCheck2 className="size-5 text-primary" />;
      case "field-maintenance":
        return <ShieldAlert className="size-5 text-primary" />;
      default:
        return <CheckCircle2 className="size-5 text-primary" />;
    }
  };
  return <section id="servicessupportsplit" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold">
              Technical Services & Support
            </Badge>
          </div>
          <h2 className="font-default text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Operational and engineering services for regulated laboratories
          </h2>
          <p className="mt-3 text-base sm:text-lg text-muted-foreground">
            Direct field engineering, adapter fabrication, and lifecycle qualification provided across Ireland and the UK.
          </p>
        </div>

        {/* 8/4 Grid Split */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Left Column: 8 Cols - Stacked Service Cards */}
          <div className="lg:col-span-8 space-y-6">
            {servicesData.map((service, i) => <motion.div key={service.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.35,
            delay: i * 0.08
          }}>
                <Card className="border border-border bg-card text-card-foreground shadow-xs hover:border-primary/40 transition-colors">
                  <CardHeader className="pb-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                          {getServiceIcon(service.id)}
                        </div>
                        <Badge variant="secondary" className="text-xs font-normal">
                          {service.badge}
                        </Badge>
                      </div>
                    </div>
                    <CardTitle className="text-xl font-semibold text-foreground pt-1">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-1">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 pb-6">
                    <div className="bg-secondary/40 rounded-md p-3.5 mb-4 border border-border/60">
                      <div className="text-xs font-semibold text-foreground mb-1 uppercase tracking-wider">
                        Key deliverables
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {service.deliverables}
                      </p>
                    </div>

                    <div className="flex items-center justify-start">
                      <Button variant="link" className="p-0 h-auto font-medium text-sm group text-primary" asChild>
                        <Link to={service.link} className="inline-flex items-center gap-1.5">
                          <span>{service.linkText}</span>
                          <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          {/* Right Column: 4 Cols - Support Summary Callout */}
          <div className="lg:col-span-4 lg:sticky lg:top-8">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4
          }} className="bg-secondary text-secondary-foreground rounded-xl p-6 sm:p-7 border border-border shadow-xs">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <FileText className="size-4 text-primary" />
                  <span>Service Overview</span>
                </div>
                
                <h3 className="text-xl font-semibold text-foreground leading-snug">
                  Structured Lifecycle Support Framework
                </h3>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  Support programs are tailored for process analytical setups, powder testing apparatus, and optical spectrometers in GMP-governed sites.
                </p>

                <Separator className="my-4 bg-border" />

                {/* Neutral Factual Benefits */}
                <div className="space-y-4">
                  {supportBenefits.map((benefit, i) => <div key={benefit.id} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {benefit.title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 leading-normal">
                          {benefit.detail}
                        </div>
                      </div>
                    </div>)}
                </div>

                <Separator className="my-4 bg-border" />

                <div className="space-y-3 pt-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="size-3.5 text-foreground/70 shrink-0" />
                    <span>Coverage hours: Mon - Fri, 08:30 - 17:30</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin className="size-3.5 text-foreground/70 shrink-0" />
                    <span>Direct engineering dispatch: Ireland & UK</span>
                  </div>
                </div>

                <div className="pt-2">
                  <Button className="w-full" asChild>
                    <Link to="/ContactPage">
                      Contact Technical Support
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}